import type { MerchCategory } from "./merchPricing";

export const LEGACY_GARMENT_SLUGS = ["classic-tee", "softstyle-tee", "college-hoodie", "crewneck-sweatshirt", "tank-top"] as const;

export function isLegacyGarmentSlug(slug: string | null | undefined): boolean {
  return (LEGACY_GARMENT_SLUGS as readonly string[]).includes(String(slug ?? "").toLowerCase());
}

export const PRINT_VARIANT_SOURCE = "artwork_cut";

export const CANONICAL_VARIANT_SOURCE = "release_cover";

const SOURCE_RANK: Record<string, number> = { release_cover: 0, logo: 1, artwork_cut: 2 };

export interface DesignIdentity {
  key: string;
  title: string;
  source: string;
  spotifyAlbumId?: string | null;
}

export interface SluggableDesign extends DesignIdentity {
  slug?: string | null;
  eligible?: boolean;
  printReady?: boolean;
  sortOrder: number;
  createdAt: number;
}

function storefrontRank(design: SluggableDesign): number {
  return design.eligible && design.printReady ? 0 : 1;
}

export function releaseNameFromTitle(title: string): string {
  return title.replace(/\s+(cover|cut|cut-?out)$/i, "").trim();
}

export function designDisplayName(design: Pick<DesignIdentity, "title" | "source">): string {
  return design.source === CANONICAL_VARIANT_SOURCE || design.source === PRINT_VARIANT_SOURCE
    ? releaseNameFromTitle(design.title)
    : design.title.trim();
}

export function hyphenSlug(value: string): string {
  return value
    .replace(/[^a-z0-9 ]/gi, "")
    .replace(/ /g, "-")
    .toLowerCase();
}

export function designSlugBase(design: Pick<DesignIdentity, "title" | "source">): string {
  return hyphenSlug(designDisplayName(design)) || "design";
}

function sourceRank(source: string): number {
  return SOURCE_RANK[source] ?? 3;
}

export function compareDesignsForSlugs(a: SluggableDesign, b: SluggableDesign): number {
  return (
    storefrontRank(a) - storefrontRank(b) ||
    sourceRank(a.source) - sourceRank(b.source) ||
    a.sortOrder - b.sortOrder ||
    a.createdAt - b.createdAt ||
    a.key.localeCompare(b.key)
  );
}

export function assignDesignSlugs(designs: SluggableDesign[]): Map<string, string> {
  const taken = new Set<string>();
  const result = new Map<string, string>();
  const ordered = [...designs].sort(compareDesignsForSlugs);
  for (const design of ordered) {
    if (design.slug) {
      taken.add(design.slug);
      result.set(design.key, design.slug);
    }
  }
  for (const design of ordered) {
    if (design.slug) continue;
    const base = designSlugBase(design);
    let candidate = base;
    let suffix = 2;
    while (taken.has(candidate)) {
      candidate = `${base}-${suffix}`;
      suffix += 1;
    }
    taken.add(candidate);
    result.set(design.key, candidate);
  }
  return result;
}

export function designGroupKey(design: Pick<DesignIdentity, "key" | "spotifyAlbumId">): string {
  return design.spotifyAlbumId ? `album:${design.spotifyAlbumId}` : `key:${design.key}`;
}

export interface DesignGroup<T extends DesignIdentity> {
  groupKey: string;
  canonical: T;
  print: T;
  variants: T[];
}

export function groupDesigns<T extends DesignIdentity>(designs: T[]): DesignGroup<T>[] {
  const groups = new Map<string, T[]>();
  for (const design of designs) {
    const key = designGroupKey(design);
    const bucket = groups.get(key);
    if (bucket) bucket.push(design);
    else groups.set(key, [design]);
  }
  return [...groups.entries()].map(([groupKey, variants]) => {
    const canonical = variants.find((entry) => entry.source === CANONICAL_VARIANT_SOURCE) ?? variants[0];
    const print = variants.find((entry) => entry.source === PRINT_VARIANT_SOURCE) ?? canonical;
    return { groupKey, canonical, print, variants };
  });
}

export interface GarmentColorLike {
  isDark: boolean;
}

export type DesignInk = "light" | "dark" | "full";

export function designInk(design: { tags: string[] } | null | undefined): DesignInk {
  if (!design) return "full";
  if (design.tags.includes("white")) return "light";
  if (design.tags.includes("black")) return "dark";
  return "full";
}

/**
 * Artwork this dark disappears on a black garment, so the default preview goes
 * on a light one. The number is the alpha weighted mean luminance of the print
 * file, measured by `scripts/measure-artwork-tone.py`; a design that has never
 * been measured keeps the old behaviour.
 */
/**
 * How much more of the artwork disappears into a black garment than into a
 * white one before the default preview moves to a light shirt. The two shares
 * are measured by `scripts/measure-artwork-tone.py`: the portion of the ink
 * that is within reach of the garment's own tone. A design that has never been
 * measured keeps the old behaviour, and an ink-tagged row always wins, because
 * a white-ink file on a white shirt is invisible whatever the averages say.
 */
export const LIGHT_GARMENT_MARGIN = 0.18;

export interface DesignTone {
  tags: string[];
  darkShare?: number | null;
  lightShare?: number | null;
}

export function prefersLightGarment(design: DesignTone | null | undefined): boolean {
  if (!design) return false;
  const ink = designInk(design);
  if (ink === "dark") return true;
  if (ink === "light") return false;
  if (typeof design.darkShare !== "number" || typeof design.lightShare !== "number") return false;
  return design.darkShare - design.lightShare > LIGHT_GARMENT_MARGIN;
}

export function pickColorFor<C extends GarmentColorLike>(product: { colors: C[] }, design: DesignTone | null | undefined): C | null {
  if (product.colors.length === 0) return null;
  if (prefersLightGarment(design)) return product.colors.find((color) => !color.isDark) ?? product.colors[0];
  return product.colors.find((color) => color.isDark) ?? product.colors[0];
}

export function isWordmarkDesign(design: Pick<DesignIdentity, "title" | "source">): boolean {
  return design.source === "logo" && /wordmark/i.test(design.title);
}

export function garmentNoun(category: MerchCategory): string {
  switch (category) {
    case "hoodie":
      return "hoodie";
    case "sweatshirt":
      return "sweatshirt";
    case "tank":
      return "tank";
    default:
      return "tee";
  }
}

export function itemPageTitle(designName: string, category: MerchCategory): string {
  return `${designName} on a ${garmentNoun(category)}`;
}

export type InkTag = "white" | "black";

export function inkTagOf(design: { tags: string[] }): InkTag | null {
  if (design.tags.includes("white")) return "white";
  if (design.tags.includes("black")) return "black";
  return null;
}

export function inkGroupKey(design: Pick<DesignIdentity, "key"> & { tags: string[] }): string {
  if (!inkTagOf(design)) return `single:${design.key}`;
  return `ink:${design.key.replace(/[:\-_](white|black)$/i, "")}`;
}

export function designFamilyKey(design: Pick<DesignIdentity, "key" | "spotifyAlbumId"> & { tags: string[] }): string {
  return design.spotifyAlbumId ? `album:${design.spotifyAlbumId}` : inkGroupKey(design);
}

export function inkDisplayName(title: string): string {
  return title.replace(/\s+in\s+(black|white)$/i, "").trim();
}

export interface InkVariant {
  key: string;
  slug: string;
  ink: DesignInk;
  cloudinaryPublicId: string | null;
  previewUrl: string;
  width: number;
  height: number;
  tags: string[];
}

export interface InkMergeable {
  key: string;
  slug: string;
  title: string;
  tags: string[];
  cloudinaryPublicId: string | null;
  previewUrl: string;
  width: number;
  height: number;
  featured: boolean;
}

const INK_RANK: Record<DesignInk, number> = { light: 0, dark: 1, full: 2 };

export function toInkVariant<T extends InkMergeable>(design: T): InkVariant {
  return {
    key: design.key,
    slug: design.slug,
    ink: designInk(design),
    cloudinaryPublicId: design.cloudinaryPublicId,
    previewUrl: design.previewUrl,
    width: design.width,
    height: design.height,
    tags: design.tags,
  };
}

export function mergeInkVariants<T extends InkMergeable>(designs: T[]): Array<T & { inks: InkVariant[] }> {
  const groups = new Map<string, T[]>();
  for (const design of designs) {
    const groupKey = inkGroupKey(design);
    const bucket = groups.get(groupKey);
    if (bucket) bucket.push(design);
    else groups.set(groupKey, [design]);
  }
  return [...groups.values()].map((variants) => {
    const ordered = [...variants].sort((a, b) => INK_RANK[designInk(a)] - INK_RANK[designInk(b)]);
    const primary = ordered[0];
    if (ordered.length === 1) return { ...primary, inks: [toInkVariant(primary)] };
    const title = inkDisplayName(primary.title);
    return {
      ...primary,
      title,
      slug: hyphenSlug(title) || primary.slug,
      featured: ordered.some((entry) => entry.featured),
      inks: ordered.map(toInkVariant),
    };
  });
}

export function inkVariantFor<V extends { ink: DesignInk }>(variants: V[], garmentIsDark: boolean): V | null {
  if (variants.length === 0) return null;
  if (variants.length === 1) return variants[0];
  const wanted: DesignInk = garmentIsDark ? "light" : "dark";
  return variants.find((variant) => variant.ink === wanted) ?? variants[0];
}
